import { Slideout } from '@jbhi-fi/components/src/Slideout/Slideout';
import React from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { JbCartItemTransformer } from './transformers/JbCartItemTransformer';
import { CartLineItem } from '@jbhi-fi/components/src/Cart/CartLineItem/CartLineItem';
import { WishlistCard } from '@jbhi-fi/components/src/Cart/WishlistCard/WishlistCard';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { styles } from './MiniCart.css';
import { CartActions } from './CartActions/CartActions';
import CartIcon from '@jbhi-fi/lanyard-ui/components/Icons/Cart';
import { BundleOffering } from '@jbhi-fi/components/src/Cart/BundleOffering/BundleOffering';
import { AttachedServicesBlock } from './CartServices/AttachedServicesBlock';
import { ServicesCarousel } from './CartServices/ServicesCarousel';
import { getCartLineServices } from './cartLineServices';

export const MiniCart = () => {
  const {
    isMiniCartOpen,
    toggleMiniCart,
    cart,
    location,
    removeItem,
    updateQuantity,
    addService,
    clearWishlistCards,
    addBundleOffering,
    removeAttachedService,
    removeAttachedServices,
  } = useCartContext();
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const isDesktop = useBreakpoint('md', 'up');

  if (location === 'cart') return null;
  return (
    <Slideout
      size={isDesktop ? '25rem' : '100%'}
      isOpen={isMiniCartOpen}
      onClose={() => toggleMiniCart(false)}
      dataTestId="mini-cart-slideout"
      headerElement={
        <h3 className={styles.title} data-testid="miniCart-header">
          My Cart
        </h3>
      }
    >
      {cart?.items.length ? (
        <>
          <div className={styles.lineItems}>
            {cart?.items
              .filter(item => !item.hide)
              .map(item => {
                const {
                  offering,
                  attachedService,
                  bundleSuggestion,
                  installSuggestions,
                  nonInstallSuggestions,
                  attachedInstallServices,
                  nonInstallServiceData,
                  shouldRenderDefaultAttachedService,
                  hasInstallModule,
                  hasServicesModule,
                  hasMultipleServiceModules,
                  shouldRenderAttachedServicesBlock,
                  productFacets,
                } = getCartLineServices({
                  cart,
                  item,
                });

                const servicesModuleConfig = {
                  variant: 'minicart' as const,
                  lineId: String(item.line_id),
                  productSku: item.sku,
                  productServiceLink: item.properties?._serviceLink,
                  facets: productFacets,
                  productCartId: item.properties?._uuid,
                  gtmData: item.onlineProduct?.ProductTags?.GoogleTagManager,
                  installSuggestions,
                  nonInstallSuggestions,
                  nonInstallServiceData,
                  trackingMessage: offering?.trackingMessage,
                  preSelectedOfferingId: attachedInstallServices[0]?.serviceData?.id,
                  onAddService: nonInstallServiceData ? () => addService(item, nonInstallServiceData) : undefined,
                };

                return (
                  <CartLineItem
                    key={item.line_id}
                    {...JbCartItemTransformer.toCartLineItemProps(item, cart.isValidated)}
                    bundleOfferings={
                      bundleSuggestion ? (
                        <BundleOffering
                          key={item.line_id}
                          location={location}
                          {...bundleSuggestion}
                          onAdd={() => addBundleOffering(item)}
                        />
                      ) : null
                    }
                    attachedServices={
                      shouldRenderAttachedServicesBlock ? (
                        <AttachedServicesBlock
                          lineId={String(item.line_id)}
                          variant="miniCart"
                          isNoLongerAvailable={item.isNoLongerAvailable}
                          attachedInstallServices={attachedInstallServices}
                          attachedService={attachedService}
                          shouldRenderDefaultAttachedService={shouldRenderDefaultAttachedService}
                          removeInstallServices={
                            attachedInstallServices.length
                              ? () =>
                                  removeAttachedServices(
                                    item,
                                    attachedInstallServices.map(entry => entry.cartItem)
                                  )
                              : undefined
                          }
                          removeAttachedService={item.attachedService ? () => removeAttachedService(item) : undefined}
                          servicesModuleConfig={servicesModuleConfig}
                        />
                      ) : null
                    }
                    onRemove={() => removeItem(item, isLoggedIn)}
                    variant="miniCart"
                    onQuantityChange={quantity => updateQuantity(item, quantity)}
                    serviceOfferings={
                      hasInstallModule || hasServicesModule ? (
                        <ServicesCarousel
                          slidesPerView={hasMultipleServiceModules ? 0.88 : 0.65}
                          {...servicesModuleConfig}
                        />
                      ) : undefined
                    }
                    wishlistCard={
                      isLoggedIn ? (
                        <WishlistCard
                          {...JbCartItemTransformer.toCartItemWishlistProps(item)}
                          location={location}
                          isSaved={getIsInWishlist(item.sku)}
                          onClose={() => clearWishlistCards()}
                          onWishlistClick={async () => {
                            await handleUpdateWishlist({
                              actionType: 'addItem',
                              sku: item.sku,
                              variantId: Number(item.id),
                            });
                          }}
                          variant="miniCart"
                        />
                      ) : undefined
                    }
                  />
                );
              })}
          </div>
          <div className={styles.summary}>
            <CartActions
              totalPrice={cart?.total_price || 0}
              totalDiscount={cart?.total_discount || 0}
              variant="miniCart"
            />
          </div>
        </>
      ) : (
        <div className={styles.emptyCart} data-testid="miniCart-empty">
          <CartIcon width={32} height={32} />
          <p>Your cart is empty</p>
        </div>
      )}
    </Slideout>
  );
};
